import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// components
import SearchModalBase from 'shared/components/SearchModal';

// styles
import * as Text from 'theme/components/Text.style';
import { ItemSubContent } from 'theme/components/Dropdown.style';

// types
import { Profile } from 'teamProfiles/_types';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  profiles: Profile[] | undefined;
  onProfileClick: (profileId: string) => void;
}

export default function SearchModal({
  isOpen,
  onClose,
  profiles,
  onProfileClick,
}: SearchModalProps): JSX.Element {
  const { t } = useTranslation('pageTeam');

  const filterItems = useCallback(
    (query: string): Profile[] => {
      if (!profiles) return [];
      const trimmed = query.trim().toLowerCase();
      if (!trimmed) return profiles;
      return profiles.filter(
        (profile) =>
          profile.full_name.toLowerCase().includes(trimmed) ||
          profile.employee_id?.toLowerCase().includes(trimmed)
      );
    },
    [profiles]
  );

  const handleItemSelect = useCallback(
    (profile: Profile) => {
      onProfileClick(profile.id);
    },
    [onProfileClick]
  );

  return (
    <SearchModalBase
      isOpen={isOpen}
      onClose={onClose}
      placeholder={t('viewAllProfiles.searchPlaceholder')}
      noResultsText={t('viewAllProfiles.noSearchResults')}
      filterItems={filterItems}
      getItemKey={(profile) => profile.id}
      renderItem={(profile) => (
        <>
          <Text.P2>{profile.full_name}</Text.P2>
          {profile.employee_id && (
            <ItemSubContent>{profile.employee_id}</ItemSubContent>
          )}
        </>
      )}
      onItemSelect={handleItemSelect}
    />
  );
}
